// A project's pages keep their project in the address, as today's do
// (projects/routing.ts there): `/p/<Key>/board`, `/p/<Key>/code`, a
// session's `/p/<Key>/code/<id>`, the Plans tab's `/p/<Key>/plans` and a
// plan's `/p/<Key>/plan/<id>`. An issue keeps its own address, its key.
// `/board` and `/code` alone are the selected project's, and move there.
// Needs you is gone (Poulpe-406): `/` is the Board, and its old addresses,
// `/needs-you` and the queue's `/queue` and `/p/<Key>/queue`, lead to the
// Board's "Ready to merge" (App.tsx, workspace/model.ts).
import { KEY_NAME } from '../lib/keys'
import type { Project, Proposal } from '../api/types'

/** The pages a project has, by the first segment after its key. */
export const PROJECT_PAGES = ['board', 'code', 'plans', 'plan', 'docs'] as const

const PROJECT_ROUTE = new RegExp(`^/p/([^/]+)(/(?:${PROJECT_PAGES.join('|')})(?:/.*)?)/?$`, 'i')
const UNQUALIFIED = new RegExp(`^/(?:${PROJECT_PAGES.join('|')})(?:/|$)`)

/** Project pages keep their project in the address; entity pages keep their own addresses. */
export function readProjectPath(path: string): { key: string; page: string } | null {
  const match = PROJECT_ROUTE.exec(path)
  if (!match) return null
  let key: string
  try { key = decodeURIComponent(match[1]!) } catch { return null }
  const page = match[2]!.replace(/^\/[a-z]+/i, kind => kind.toLowerCase())
  return KEY_NAME.test(key) ? { key, page } : null
}

export function projectPath(key: string, page: string): string {
  return `/p/${key}${page}`
}

/** A plan's page, in its project. */
export function planPath(key: string, plan: string): string {
  return projectPath(key, `/plan/${encodeURIComponent(plan)}`)
}

/** A session's page: in its project's Code tab. */
export function sessionPath(key: string, session: string): string {
  return projectPath(key, `/code/${encodeURIComponent(session)}`)
}

export function projectForPath(path: string, projects: readonly Project[]): Project | undefined {
  const route = readProjectPath(path)
  return route ? projects.find(project => project.key.toLowerCase() === route.key.toLowerCase()) : undefined
}

export function projectPage(path: string): string {
  return readProjectPath(path)?.page ?? path
}

/** A project page's address without its project (`/board`) is the selected project's, or the first's. */
export function movedProjectPath(path: string, search: string, hash: string, projects: readonly Project[], selected: string | null): string | null {
  const route = readProjectPath(path)
  if (!route && !UNQUALIFIED.test(path)) return null
  const project = route ? projectForPath(path, projects) : projects.find(project => project.id === selected) ?? projects[0]
  if (!project) return null
  const next = projectPath(project.key, route?.page ?? path) + search + hash
  return next === path + search + hash ? null : next
}

/** Needs you's old address, and the queue's (`/p/<Key>/queue` keeps its project's key). */
const OLD_PAGE = /^\/(?:needs-you|queue|p\/([^/]+)\/queue)\/?$/i

/**
 * Where an old address leads now: `/` to the Board, `/needs-you` and the
 * queue's to its "Ready to merge", which a link to one proposal
 * (`#proposal-<id>`) still reaches. The selected project's Board, or
 * `/board` while none is, which ProjectRoute moves on. An old query is
 * dropped. Nothing for any other address, nor for a bare `/` unless `home`.
 */
export function boardForOld(path: string, hash: string, projects: readonly Project[], selected: string | null, home = true, proposals: readonly Proposal[] = []): string | null {
  const proposal = hash.startsWith('#proposal-')
  let key: string | undefined
  if (path === '/') {
    if (!home && !proposal) return null
  } else {
    const match = OLD_PAGE.exec(path)
    if (!match) return null
    try { key = match[1] && decodeURIComponent(match[1]) } catch { key = undefined }
  }
  const named = key === undefined ? undefined : projects.find(project => project.key.toLowerCase() === key.toLowerCase())
  const linked = proposal ? proposals.find(p => hash === `#proposal-${p.id}`) : undefined
  const project = named ?? projects.find(project => project.id === linked?.project_id) ?? projects.find(project => project.id === selected)
  const ready = path !== '/' || proposal
  return (project ? projectPath(project.key, '/board') : '/board') + (ready ? '?live=ready' : '') + hash
}
